// frontend/src/pages/dashboard/MyDashboard.js
//
// The employee-facing overview. Managers and above get TeamDashboard instead —
// see pages/Dashboard.js.
//
// This page deliberately does NOT call getDashboardStats: that endpoint is
// require_role(admin|manager|dept_head), so an employee got a 403 which the old
// shared dashboard swallowed into a page reading "Team members: 1". Every source
// below is one an employee can already read.
//
// Panels are declared in PANELS and filtered by the user's program, exactly as
// the sidebar filters modules. They are NOT in content/modules.js — a panel is
// not a navigable module — but the ones that link somewhere resolve their label
// and route through moduleByKey so a rename still propagates.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  getCycles, getKPIAssignments, getOKRs, getCheckins,
  getFeedback, getReviews, getJournal, getActionPlans,
  getCycleEligibility, diagnoseSetup,
} from '../../services/api';
import { Card, Badge, ProgressBar, SectionTitle, Spinner, Btn, scoreToLabel } from '../../components/UI';
import PageHeader from '../../components/PageHeader';
import { moduleByKey } from '../../content/modules';
import { currentMondayISO } from '../../utils/week';

const SALES_PROGRAM_ID = 2;

function formatDate(d) {
  if (!d) return '';
  return new Date(String(d).length <= 10 ? `${d}T00:00:00` : d)
    .toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
}
function formatDateTime(d) {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }) +
    ' ' + dt.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
}
/** Whole days from today to a YYYY-MM-DD date; negative if past. */
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(`${String(dateStr).slice(0, 10)}T00:00:00`);
  const today = new Date();
  const midnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((target - midnight) / 86400000);
}

/** A labelled panel with a consistent frame. */
function Panel({ title, action, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <SectionTitle>{title}</SectionTitle>
        {action}
      </div>
      {children}
    </div>
  );
}

/** Muted "nothing here yet" line — a new employee legitimately has none of this. */
function Nothing({ children }) {
  return (
    <Card style={{ padding: '20px 16px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
      {children}
    </Card>
  );
}

export default function MyDashboard() {
  const { user } = useAuth();
  const [loading, setLoading]   = useState(true);
  const [cycle, setCycle]       = useState(null);
  const [kpis, setKpis]         = useState([]);
  const [okrs, setOkrs]         = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [reviews, setReviews]   = useState([]);
  const [journal, setJournal]   = useState(null);
  const [plans, setPlans]       = useState([]);
  // Set when the viewer is marked not eligible for the active cycle. They see the
  // STATUS but never the reason — free text typed for an audit trail isn't
  // necessarily a sentence to render on someone's home screen.
  const [notEligible, setNotEligible] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);   // why no KPIs, if none

  const isSales = Number(user?.program_id) === SALES_PROGRAM_ID;

  useEffect(() => {
    let alive = true;
    (async () => {
      const cycles = await getCycles().catch(() => []);
      // The cycle in play: the active one, else the most recent by start date
      // (getCycles already orders start_date DESC).
      const list = Array.isArray(cycles) ? cycles : [];
      const active = list.find(c => c.status === 'active') || list[0] || null;
      if (!alive) return;
      setCycle(active);

      const cycleId = active?.id;
      const [k, o, c, f, r, j, p, excl, diag] = await Promise.all([
        cycleId ? getKPIAssignments({ cycle_id: cycleId }).catch(() => []) : [],
        getOKRs().catch(() => []),
        getCheckins().catch(() => []),
        getFeedback().catch(() => []),
        getReviews().catch(() => []),
        isSales && cycleId ? getJournal(cycleId, user?.id).catch(() => null) : null,
        isSales && cycleId ? getActionPlans({ cycle_id: cycleId }).catch(() => []) : [],
        cycleId ? getCycleEligibility(cycleId).catch(() => []) : [],
        cycleId ? diagnoseSetup({ cycle_id: cycleId }).catch(() => null) : null,
      ]);
      if (!alive) return;
      setKpis(Array.isArray(k) ? k : []);
      setOkrs(Array.isArray(o) ? o : []);
      setCheckins(Array.isArray(c) ? c : []);
      setFeedback(Array.isArray(f) ? f : []);
      setReviews(Array.isArray(r) ? r : []);
      setJournal(j);
      setPlans(Array.isArray(p) ? p : []);
      setNotEligible((Array.isArray(excl) ? excl : [])
        .some(x => String(x.employee_id) === String(user?.id)));
      setDiagnosis(diag);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [user?.id, isSales]);

  if (loading) return <Spinner />;

  // ── Self-evaluation progress ──────────────────────────────────────────────
  const kpiTotal    = kpis.length;
  const selfDone    = kpis.filter(k => k.self_score != null).length;
  const mgrDone     = kpis.filter(k => k.manager_score != null).length;
  const allFinalised = kpiTotal > 0 && mgrDone === kpiTotal;
  const selfPct     = kpiTotal ? Math.round((selfDone / kpiTotal) * 100) : 0;

  // ── Next milestone deadline. Only surfaced by the reminder cron until now. ──
  const MILESTONES = [
    ['kpi_review_date',      'KPI setup'],
    ['self_assessment_date', 'Your self-assessment'],
    ['manager_review_date',  'Manager assessment'],
  ];
  const nextMilestone = cycle
    ? MILESTONES
        .map(([field, label]) => ({ label, date: cycle[field], days: daysUntil(cycle[field]) }))
        .filter(m => m.date && m.days != null && m.days >= 0)
        .sort((a, b) => a.days - b.days)[0]
    : null;

  // ── My OKRs (own only — the endpoint already scopes an employee to self) ───
  const myOkrs   = okrs.filter(o => o.owner_type !== 'org');
  const onTrack  = myOkrs.filter(o => o.status === 'on_track').length;
  const atRisk   = myOkrs.filter(o => o.status === 'at_risk').length;

  const nextCheckin = checkins
    .filter(c => c.status === 'scheduled')
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))[0];

  const feedbackReceived = feedback.filter(f => String(f.to_user_id) === String(user?.id));

  const latestReview = reviews
    .filter(r => r.status === 'submitted')
    .sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at))[0];

  // ── Sales: this week's journal entry ──────────────────────────────────────
  const thisMonday   = currentMondayISO();
  const journalDone  = (journal?.entries || [])
    .some(e => String(e.week_start).slice(0, 10) === thisMonday);

  // ── Sales: this month's action plan row ───────────────────────────────────
  const PLAN_FIELDS = ['targets', 'pipeline_management', 'activity_commitment', 'support'];
  const thisMonthPlan = plans.find(p => {
    const label = String(p.month_label || '').toLowerCase();
    const now = new Date().toLocaleDateString('en-AU', { month: 'long' }).toLowerCase();
    return label.includes(now);
  }) || plans[0] || null;
  const planFilled = thisMonthPlan
    ? PLAN_FIELDS.filter(f => thisMonthPlan[f] && String(thisMonthPlan[f]).trim()).length
    : 0;

  const kpiScoring = moduleByKey('kpi-scoring');
  const okrMod     = moduleByKey('okrs');
  const checkinMod = moduleByKey('checkins');
  const feedbackMod = moduleByKey('feedback');
  const reviewsMod = moduleByKey('reviews');
  const journalMod = moduleByKey('sales-journal');
  const plansMod   = moduleByKey('sales-action-plans');

  const PANELS = [
    // ── Shared ───────────────────────────────────────────────────────────────
    {
      key: 'next-deadline', programId: null, hideWhenNotEligible: true,
      render: () => (
        <Panel key="next-deadline" title="Next deadline">
          {nextMilestone ? (
            <Card style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{
                width: 46, height: 46, borderRadius: 10, flexShrink: 0,
                background: nextMilestone.days <= 3 ? 'var(--amber-50, #FDF6E3)' : 'var(--navy-50)',
                color: nextMilestone.days <= 3 ? 'var(--amber-700)' : 'var(--navy-600)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ fontSize: 17, fontWeight: 600, lineHeight: 1 }}>{nextMilestone.days}</span>
                <span style={{ fontSize: 9 }}>{nextMilestone.days === 1 ? 'day' : 'days'}</span>
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{nextMilestone.label}</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
                  Due {formatDate(nextMilestone.date)} · {cycle?.name}
                </div>
              </div>
            </Card>
          ) : (
            <Nothing>No deadlines set for {cycle?.name || 'the current cycle'}.</Nothing>
          )}
        </Panel>
      ),
    },
    {
      key: 'not-eligible', programId: null, onlyWhenNotEligible: true,
      render: () => (
        <Panel key="not-eligible" title="This cycle">
          <Card style={{ padding: '14px 16px' }}>
            <Badge color="gray">Not eligible this cycle</Badge>
            <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 8, lineHeight: 1.6 }}>
              You are not being assessed for {cycle?.name || 'this cycle'}, so there is no
              self-evaluation or review to complete. Speak to your manager if that looks wrong.
            </div>
          </Card>
        </Panel>
      ),
    },
    {
      key: 'self-eval', programId: null, hideWhenNotEligible: true,
      render: () => (
        <Panel key="self-eval" title="Your self-evaluation"
          action={<Link to={kpiScoring.route} style={{ textDecoration: 'none', fontSize: 12, color: 'var(--blue-700)', fontWeight: 500 }}>{kpiScoring.label} &rarr;</Link>}>
          {kpiTotal === 0 ? (
            <Nothing>
              {diagnosis?.blocked
                ? <>{diagnosis.message}{' '}
                    <Link to={`/guide#task-${diagnosis.task_id}`} style={{ color: 'var(--blue-700)' }}>
                      What this means
                    </Link>
                  </>
                : <>No KPIs assigned for {cycle?.name || 'this cycle'} yet.</>}
            </Nothing>
          ) : (
            <Card style={{ padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 500 }}>
                  {selfDone} of {kpiTotal} KPIs self-scored
                </span>
                <Badge color={allFinalised ? 'green' : selfDone === kpiTotal ? 'blue' : 'amber'}>
                  {allFinalised ? 'Finalised' : selfDone === kpiTotal ? 'Awaiting your manager' : 'In progress'}
                </Badge>
              </div>
              <ProgressBar value={selfPct} />
              {selfDone < kpiTotal && (
                <Link to={kpiScoring.route} style={{ textDecoration: 'none' }}>
                  <Btn size="sm" style={{ marginTop: 12 }}>
                    {selfDone === 0 ? 'Start self-evaluation' : 'Continue self-evaluation'}
                  </Btn>
                </Link>
              )}
            </Card>
          )}
        </Panel>
      ),
    },
    // ── Sales-only ───────────────────────────────────────────────────────────
    {
      key: 'journal-week', programId: SALES_PROGRAM_ID,
      render: () => (
        <Panel key="journal-week" title="This week's journal">
          <Card style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Badge color={journalDone ? 'green' : 'amber'}>
              {journalDone ? 'Written' : 'Not written yet'}
            </Badge>
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
              Week of {formatDate(thisMonday)}
              {!journalDone && ' · closes at the end of this week'}
            </span>
            <Link to={journalMod.route} style={{ textDecoration: 'none', marginLeft: 'auto' }}>
              <Btn size="sm" variant={journalDone ? 'secondary' : 'primary'}>
                {journalDone ? 'Review entry' : "Write this week's entry"}
              </Btn>
            </Link>
          </Card>
        </Panel>
      ),
    },
    {
      key: 'action-plan', programId: SALES_PROGRAM_ID,
      render: () => (
        <Panel key="action-plan" title="This month's action plan">
          {thisMonthPlan ? (
            <Card style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Badge color={planFilled === PLAN_FIELDS.length ? 'green' : planFilled ? 'amber' : 'gray'}>
                {planFilled} of {PLAN_FIELDS.length} sections filled
              </Badge>
              <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{thisMonthPlan.month_label}</span>
              <Link to={plansMod.route} style={{ textDecoration: 'none', marginLeft: 'auto' }}>
                <Btn size="sm" variant={planFilled === PLAN_FIELDS.length ? 'secondary' : 'primary'}>
                  {planFilled === PLAN_FIELDS.length ? 'View plan' : 'Complete plan'}
                </Btn>
              </Link>
            </Card>
          ) : (
            <Nothing>No action plan for {cycle?.name || 'this cycle'} yet.</Nothing>
          )}
        </Panel>
      ),
    },
    // ── Shared, lower ────────────────────────────────────────────────────────
    {
      key: 'okrs', programId: null,
      render: () => (
        <Panel key="okrs" title="Your OKRs"
          action={<Link to={okrMod.route} style={{ textDecoration: 'none', fontSize: 12, color: 'var(--blue-700)', fontWeight: 500 }}>View all &rarr;</Link>}>
          {myOkrs.length === 0 ? (
            <Nothing>No objectives set yet.</Nothing>
          ) : (
            <Card style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ display: 'flex', gap: 18, padding: '12px 16px', borderBottom: '0.5px solid var(--border)' }}>
                <span style={{ fontSize: 12, color: 'var(--text-2)' }}><b>{onTrack}</b> on track</span>
                <span style={{ fontSize: 12, color: atRisk ? 'var(--amber-700)' : 'var(--text-2)' }}><b>{atRisk}</b> at risk</span>
                <span style={{ fontSize: 12, color: 'var(--text-3)', marginLeft: 'auto' }}>{myOkrs.length} total</span>
              </div>
              {myOkrs.slice(0, 3).map(o => (
                <div key={o.id} style={{ padding: '11px 16px', borderBottom: '0.5px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
                    <span style={{ fontSize: 13 }}>{o.title}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-3)', flexShrink: 0 }}>{o.progress || 0}%</span>
                  </div>
                  <ProgressBar value={o.progress || 0} />
                </div>
              ))}
            </Card>
          )}
        </Panel>
      ),
    },
    {
      key: 'checkin', programId: null,
      render: () => (
        <Panel key="checkin" title="Next check-in"
          action={<Link to={checkinMod.route} style={{ textDecoration: 'none', fontSize: 12, color: 'var(--blue-700)', fontWeight: 500 }}>View all &rarr;</Link>}>
          {nextCheckin ? (
            <Card style={{ padding: '14px 16px' }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{formatDateTime(nextCheckin.scheduled_at)}</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 3 }}>
                with {nextCheckin.manager_name || 'your manager'}
                {nextCheckin.location && ` · ${nextCheckin.location}`}
              </div>
            </Card>
          ) : (
            <Nothing>No check-in scheduled.</Nothing>
          )}
        </Panel>
      ),
    },
    {
      key: 'feedback', programId: null,
      render: () => (
        <Panel key="feedback" title="Feedback received"
          action={<Link to={feedbackMod.route} style={{ textDecoration: 'none', fontSize: 12, color: 'var(--blue-700)', fontWeight: 500 }}>{feedbackMod.label} &rarr;</Link>}>
          {feedbackReceived.length === 0 ? (
            <Nothing>No feedback received yet.</Nothing>
          ) : (
            <Card style={{ padding: '14px 16px' }}>
              <span style={{ fontSize: 13 }}>
                <b>{feedbackReceived.length}</b> {feedbackReceived.length === 1 ? 'response' : 'responses'}
              </span>
            </Card>
          )}
        </Panel>
      ),
    },
    {
      key: 'latest-review', programId: null,
      render: () => (
        <Panel key="latest-review" title="Your latest review"
          action={<Link to={reviewsMod.route} style={{ textDecoration: 'none', fontSize: 12, color: 'var(--blue-700)', fontWeight: 500 }}>All reviews &rarr;</Link>}>
          {latestReview ? (
            <Card style={{ padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                {latestReview.overall_score != null && (
                  <span style={{ fontSize: 19, fontWeight: 600 }}>
                    {Number(latestReview.overall_score).toFixed(2)}
                    <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 400 }}>/5</span>
                  </span>
                )}
                {latestReview.overall_score != null && (
                  <Badge color="blue">{scoreToLabel(Number(latestReview.overall_score))}</Badge>
                )}
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{latestReview.cycle_name}</span>
              </div>
              {/* Employees have no UI link into the review form, so this listing is
                  their only view of summary_notes — render it in full, never
                  ellipsis-truncated. */}
              {latestReview.summary_notes && (
                <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
                  {latestReview.summary_notes}
                </div>
              )}
            </Card>
          ) : (
            <Nothing>No reviews yet.</Nothing>
          )}
        </Panel>
      ),
    },
  ];

  const program = Number(user?.program_id) || null;
  const panels = PANELS
    .filter(p => p.programId == null || Number(p.programId) === program)
    // The not-eligible notice appears ONLY when excluded; the self-eval prompt and
    // the deadline countdown drop out then, so the page never asks for work the
    // person doesn't owe.
    .filter(p => (p.onlyWhenNotEligible ? notEligible : true))
    .filter(p => !(notEligible && p.hideWhenNotEligible));

  return (
    <div style={{ padding: '20px clamp(16px, 3vw, 32px)', maxWidth: 780 }}>
      <PageHeader moduleKey="home"
        subtitle={`${cycle?.name || user?.department_name || 'No active cycle'} · ${user?.name}`} />
      {panels.map(p => p.render())}
    </div>
  );
}
